/*
 * <free-ship-timer> (snippets/free-ship-timer.liquid): each visitor's 10-minute free standard shipping offer.
 *
 * The first time the offer is shown, the end time is saved in localStorage ("sunday:free-ship") and the giveaway
 * portal makes the visitor a personal free shipping code that Shopify expires at that same end time
 * (giveaway.sundayaus.com/api/free-shipping). The code is added to their cart, so checkout shows standard shipping
 * free until the time is up, then normal rates. The countdown only shows once the code is in place, so it never
 * promises something checkout won't give. Once it's run out it stays hidden until "offer again after" days pass.
 * Testing: add ?freeship=reset to any page to start a new offer in this browser.
 */
(() => {
  if (customElements.get('free-ship-timer')) return;

  const KEY = 'sunday:free-ship';
  const now = () => Date.now();

  const read = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || null;
    } catch {
      return null;
    }
  };
  const save = (offer) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(offer));
    } catch {}
  };

  try {
    if (new URLSearchParams(location.search).get('freeship') === 'reset') localStorage.removeItem(KEY);
  } catch {}

  // The visitor's offer: { start, end, code?, failed? }. Starts one now if they've never had one (or it's due again).
  function offer(minutes, repeatDays) {
    const saved = read();
    if (saved && (saved.end > now() || now() - saved.end < repeatDays * 86400000)) return saved;
    const fresh = { start: now(), end: now() + minutes * 60000 };
    save(fresh);
    return fresh;
  }

  const root = () => window.Shopify?.routes?.root || '/';
  const isCountdownCode = (code) => /^FREESHIP-/i.test(code);

  // Sets the cart's discount codes: keeps the visitor's other codes (e.g. their $10 welcome code), drops any
  // countdown code except `keep`, so an expired one never shows up as "not valid" at checkout.
  async function setCountdownCode(keep, endsAt) {
    const cart = await fetch(`${root()}cart.js`).then((r) => r.json());
    const codes = (cart.discount_codes || []).map((d) => d.code);
    const others = codes.filter((c) => !isCountdownCode(c));
    const wanted = keep ? [...others, keep] : others;
    if (wanted.length === codes.length && wanted.every((c) => codes.some((x) => x.toUpperCase() === c.toUpperCase()))) return;
    await fetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        discount: wanted.join(','),
        attributes: { _free_ship_ends: endsAt ? new Date(endsAt).toISOString() : '' }, // read by the checkout block
      }),
    });
  }

  // Gets the visitor's code (once) and makes sure it's on their cart. Shared by every timer on the page.
  let ready = null;
  function ensureCode(current, endpoint) {
    ready ??= (async () => {
      if (current.failed || current.end <= now()) {
        await setCountdownCode(null).catch(() => {}); // time's up: tidy any old code off the cart
        return null;
      }
      if (!current.code) {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endsAt: current.end }),
        });
        if (!res.ok) throw new Error(`free shipping code ${res.status}`);
        const data = await res.json();
        Object.assign(current, { code: data.code, end: Math.min(current.end, data.endsAt) });
        save(current);
      }
      await setCountdownCode(current.code, current.end);
      return current;
    })().catch((error) => {
      console.warn('[free-ship-timer]', error);
      current.failed = true; // no code, no countdown
      save(current);
      return null;
    });
    return ready;
  }

  class FreeShipTimer extends HTMLElement {
    async connectedCallback() {
      this.clock = this.querySelector('[data-free-timer-clock]');
      this.fill = this.querySelector('[data-free-timer-fill]');
      const current = offer(Number(this.dataset.minutes) || 10, Number(this.dataset.repeatDays) || 30);
      this.offer = window.Shopify?.designMode ? current : await ensureCode(current, this.dataset.endpoint);
      if (!this.offer || !this.isConnected) return;
      this.update();
      this.interval = setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    update() {
      const remaining = this.offer.end - now();
      if (remaining <= 0) {
        this.hidden = true; // time's up (Shopify has expired the code too)
        clearInterval(this.interval);
        if (!FreeShipTimer.tidied) {
          FreeShipTimer.tidied = true;
          setCountdownCode(null).catch(() => {});
        }
        return;
      }
      const seconds = Math.ceil(remaining / 1000);
      this.clock.textContent = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
      if (this.fill) this.fill.style.width = `${(remaining / (this.offer.end - this.offer.start)) * 100}%`;
      this.hidden = false;
    }
  }

  customElements.define('free-ship-timer', FreeShipTimer);
})();
